import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { User, UserSubscription } from '../types';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Create Supabase client with persistence enabled
const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storage: window.localStorage,
    storageKey: 'sb-auth-token',
    detectSessionInUrl: true,
    flowType: 'implicit'
  },
  global: {
    headers: {
      'X-Client-Info': 'enkiai-web'
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Debug helper to check Supabase configuration
if (import.meta.env.DEV) {
  console.log('Supabase Configuration:', {
    url: supabaseUrl,
    hasAnonKey: !!supabaseAnonKey,
    persistSession: true,
    storageKey: 'sb-auth-token'
  });
}

// Helper function to clean up old tokens
const cleanupOldTokens = () => {
  // List of old tokens to remove
  const oldTokens = [
    'enkiai-auth-token',
    'supabase.auth.token',
    'enkiai-session'
  ];
  
  // Remove only the old tokens
  oldTokens.forEach(token => {
    localStorage.removeItem(token);
  });
};

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  forceClearAllData: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  updateSubscription: (subscription: Partial<UserSubscription>) => void;
  isLoading: boolean;
  canUseAI: (type?: 'premium' | 'standard') => boolean;
  canUploadFile: (fileSize: number) => boolean;
  incrementAIUsage: (type?: 'premium' | 'standard') => void;
  addStorageUsage: (size: number) => void;
  error: string | null;
  setError: (error: string | null) => void;
  setIsLoading: (loading: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initializationAttempts = useRef(0);
  const isInitializing = useRef(true);

  // Clean up old tokens on mount
  useEffect(() => {
    cleanupOldTokens();
  }, []);

  // Helper function to handle session data with progressive loading
  const handleSession = async (session: any) => {
    try {
      console.log('Starting handleSession for user:', session.user.email);

      // Use the session data we already have
      const authUser = session.user;
      console.log('Using existing session data');

      // Create initial user object with basic data
      const initialUserObject: User = {
        id: authUser.id,
        email: authUser.email || '',
        name: authUser.user_metadata?.name || '',
        avatar: undefined,
        settings: {
          theme: 'latte',
          autoSave: true,
          showPreview: true,
          deepseekApiKey: undefined
        },
        subscription: {
          plan: 'free',
          status: 'active',
          currentPeriodEnd: undefined,
          cancelAtPeriodEnd: false,
          billingCycle: 'monthly',
          usage: {
            storageUsed: 0,
            aiQueriesUsed: 0,
            premiumQueriesUsed: 0,
            aiQueriesResetDate: new Date()
          }
        }
      };

      // Set initial state to unblock the UI
      console.log('Setting initial user state...');
      setUser(initialUserObject);
      setError(null);
      setIsLoading(false);

      // Load additional data in the background
      console.log('Loading additional data in background...');
      try {
        const [profileResult, settingsResult, subscriptionResult] = await Promise.all([
          supabase.from('user_profiles').select('*').eq('id', authUser.id).single(),
          supabase.from('user_settings').select('*').eq('id', authUser.id).single(),
          supabase.from('user_subscriptions').select('*').eq('id', authUser.id).single()
        ]);

        console.log('Background data fetch results:', {
          hasProfileError: !!profileResult.error,
          hasSettingsError: !!settingsResult.error,
          hasSubscriptionError: !!subscriptionResult.error
        });

        // Update user object with fetched data
        const updatedUserObject: User = {
          ...initialUserObject,
          name: profileResult.error ? initialUserObject.name : profileResult.data.name,
          avatar: profileResult.error ? null : profileResult.data.avatar_url,
          settings: settingsResult.error ? initialUserObject.settings : {
            theme: settingsResult.data.theme,
            autoSave: settingsResult.data.auto_save,
            showPreview: settingsResult.data.show_preview,
            deepseekApiKey: settingsResult.data.deepseek_api_key
          },
          subscription: subscriptionResult.error ? initialUserObject.subscription : {
            plan: subscriptionResult.data.plan,
            status: subscriptionResult.data.status,
            currentPeriodEnd: subscriptionResult.data.current_period_end,
            cancelAtPeriodEnd: subscriptionResult.data.cancel_at_period_end,
            billingCycle: subscriptionResult.data.billing_cycle,
            usage: {
              storageUsed: subscriptionResult.data.storage_used,
              aiQueriesUsed: subscriptionResult.data.ai_queries_used,
              premiumQueriesUsed: subscriptionResult.data.premium_queries_used,
              aiQueriesResetDate: subscriptionResult.data.ai_queries_reset_date
            }
          }
        };

        // Update state with complete data
        console.log('Updating user state with complete data...');
        setUser(updatedUserObject);

        // Update theme if needed
        if (updatedUserObject.settings.theme) {
          localStorage.setItem('enki-theme', updatedUserObject.settings.theme);
        }
      } catch (err) {
        console.error('Error loading additional data:', err);
        // Keep using initial user object if background load fails
      }

      console.log('handleSession completed successfully');
    } catch (err) {
      console.error('Error in handleSession:', err);
      setUser(null);
      setError('Error loading user data');
      setIsLoading(false);
    }
  };

  // Set up auth state change listener
  useEffect(() => {
    console.log('Setting up auth state listener...');
    let mounted = true;
    let authTimeout: NodeJS.Timeout;
    let isInitializing = true;
    let lastSessionId: string | null = null;

    // Helper function to prevent duplicate session handling
    const handleSessionSafely = async (session: any) => {
      // Skip if unmounted or duplicate session
      if (!mounted || (session?.user?.id === lastSessionId && !isInitializing)) {
        console.log('Skipping duplicate session handling');
        return;
      }

      // Update last session ID
      lastSessionId = session?.user?.id || null;
      
      try {
        await handleSession(session);
      } finally {
        isInitializing = false;
      }
    };

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, !!session, mounted);
      
      if (!mounted) {
        console.log('Component unmounted, skipping update');
        return;
      }

      // Clear any existing timeouts
      if (authTimeout) {
        clearTimeout(authTimeout);
      }

      try {
        switch (event) {
          case 'SIGNED_OUT':
            console.log('User signed out, clearing data...');
            setUser(null);
            setIsLoading(false);
            cleanupOldTokens();
            break;

          case 'SIGNED_IN':
          case 'TOKEN_REFRESHED':
          case 'USER_UPDATED':
            console.log('User session updated:', event);
            if (session) {
              await handleSessionSafely(session);
            }
            break;

          case 'INITIAL_SESSION':
            console.log('Initial session check:', !!session);
            if (session) {
              console.log('Initial session found for:', session.user.email);
              await handleSessionSafely(session);
            } else {
              console.log('No initial session found');
              setUser(null);
              setIsLoading(false);
            }
            break;

          default:
            console.log('Unhandled auth event:', event);
            break;
        }
      } catch (err) {
        console.error('Error handling auth state change:', err);
        setError('Error updating authentication state');
        setIsLoading(false);
      }
    });

    // Initial session check with timeout
    const initializeAuth = async () => {
      console.log('Starting initial auth check...');
      if (!mounted) {
        console.log('Component unmounted during initialization');
        return;
      }

      // Set a timeout to ensure we don't get stuck loading
      authTimeout = setTimeout(() => {
        if (mounted) {
          console.log('Auth initialization timeout, setting default state');
          setUser(null);
          setError('Authentication timeout');
          setIsLoading(false);
        }
      }, 10000);

      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error during initialization:', sessionError);
          throw sessionError;
        }
        
        if (session) {
          console.log('Found existing session:', session.user.email);
          await handleSessionSafely(session);
        } else {
          console.log('No existing session found');
          setUser(null);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        setError('Error initializing authentication');
        setIsLoading(false);
      }
    };

    initializeAuth();

    return () => {
      console.log('Cleaning up auth state listener...');
      mounted = false;
      if (authTimeout) {
        clearTimeout(authTimeout);
      }
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      // Clean up any existing tokens first
      cleanupOldTokens();
      
      // Validate input
      if (!email || !password) {
        throw new Error('Por favor ingresa tu email y contraseña');
      }

      // Try to sign in
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password
      });

      if (error) {
        console.error('Detailed login error:', error);
        // Translate common errors to Spanish
        if (error.message === 'Invalid login credentials') {
          throw new Error('Email o contraseña incorrectos');
        } else if (error.message.includes('Email not confirmed')) {
          throw new Error('Por favor confirma tu email antes de iniciar sesión');
        } else if (error.message.includes('rate limit')) {
          throw new Error('Demasiados intentos. Por favor espera unos minutos');
        } else {
          throw error;
        }
      }

      if (!data.user) {
        throw new Error('No se pudo obtener la información del usuario');
      }

      // Session will be handled by the auth state change listener
      console.log('Login successful for:', data.user.email);
    } catch (err: any) {
      console.error('Login error:', err);
      // Set a user-friendly error message
      setError(err.message || 'Error al iniciar sesión. Por favor intenta de nuevo.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (email: string, password: string, name: string) => {
    setIsLoading(true);
    setError(null);
    try {
      // Sign up with Supabase
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name
          }
        }
      });

      if (error) {
        throw error;
      }

      if (!data.user) {
        throw new Error('No user returned from signup');
      }

      // User data will be loaded by the auth state change listener and the database trigger
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const forceClearAllData = async () => {
    try {
      setIsLoading(true);
      
      // Clear Supabase session
      await supabase.auth.signOut();
      
      // Clear all localStorage
      localStorage.clear();
      
      // Clear all sessionStorage
      sessionStorage.clear();
      
      // Clear all cookies
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      // Clear IndexedDB
      const databases = await window.indexedDB.databases();
      await Promise.all(
        databases.map(db => {
          if (db.name) {
            const dbName = db.name; // Assign to variable to satisfy TypeScript
            return new Promise((resolve) => {
              const request = window.indexedDB.deleteDatabase(dbName);
              request.onsuccess = () => resolve(undefined);
              request.onerror = () => resolve(undefined);
            });
          }
          return Promise.resolve();
        })
      );
      
      // Clear Cache Storage if available
      if ('caches' in window) {
        const cacheKeys = await caches.keys();
        await Promise.all(cacheKeys.map(key => caches.delete(key)));
      }

      // Reset all state
      setUser(null);
      setError(null);
      
      // Only reload if we're not already on the login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    } catch (err) {
      console.error('Error clearing data:', err);
      setError('Error clearing data. Please try refreshing the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setError(null);
    setIsLoading(true);
    try {
      // Use the forceClearAllData function for a thorough cleanup
      await forceClearAllData();
    } catch (err: any) {
      console.error('Logout error:', err);
      setError(err.message);
      setIsLoading(false);
      
      // If normal logout fails, force a hard reload
      window.location.reload();
    }
  };

  const updateUser = async (updates: Partial<User>) => {
    if (!user) return;
    
    setError(null);
    try {
      // Update profile if needed
      if (updates.name || updates.avatar) {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .update({
            name: updates.name || user.name,
            avatar_url: updates.avatar || user.avatar
          })
          .eq('id', user.id);

        if (profileError) {
          throw profileError;
        }
      }

      // Update settings if needed
      if (updates.settings) {
        const { error: settingsError } = await supabase
          .from('user_settings')
          .update({
            theme: updates.settings.theme || user.settings.theme,
            auto_save: updates.settings.autoSave !== undefined ? updates.settings.autoSave : user.settings.autoSave,
            show_preview: updates.settings.showPreview !== undefined ? updates.settings.showPreview : user.settings.showPreview,
            deepseek_api_key: updates.settings.deepseekApiKey || user.settings.deepseekApiKey
          })
          .eq('id', user.id);

        if (settingsError) {
          throw settingsError;
        }
      }

      // Update local user state
      const updatedUser = { 
        ...user, 
        ...updates,
        // Ensure settings are properly merged
        settings: {
          ...user.settings,
          ...(updates.settings || {})
        },
        // Ensure subscription is properly merged
        subscription: {
          ...user.subscription,
          ...(updates.subscription || {})
        }
      };
      
      setUser(updatedUser);
      
      // If theme was updated, also update localStorage theme
      if (updates.settings?.theme) {
        localStorage.setItem('enki-theme', updates.settings.theme);
      }
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.message);
    }
  };

  const updateSubscription = async (subscriptionUpdates: Partial<UserSubscription>) => {
    if (!user) return;
    
    setError(null);
    try {
      // Update subscription in database
      const updateData: any = {};
      
      if (subscriptionUpdates.plan) updateData.plan = subscriptionUpdates.plan;
      if (subscriptionUpdates.status) updateData.status = subscriptionUpdates.status;
      if (subscriptionUpdates.currentPeriodEnd) updateData.current_period_end = subscriptionUpdates.currentPeriodEnd;
      if (subscriptionUpdates.cancelAtPeriodEnd !== undefined) updateData.cancel_at_period_end = subscriptionUpdates.cancelAtPeriodEnd;
      if (subscriptionUpdates.billingCycle) updateData.billing_cycle = subscriptionUpdates.billingCycle;
      
      // Handle usage updates
      if (subscriptionUpdates.usage) {
        if (subscriptionUpdates.usage.storageUsed !== undefined) updateData.storage_used = subscriptionUpdates.usage.storageUsed;
        if (subscriptionUpdates.usage.aiQueriesUsed !== undefined) updateData.ai_queries_used = subscriptionUpdates.usage.aiQueriesUsed;
        if (subscriptionUpdates.usage.premiumQueriesUsed !== undefined) updateData.premium_queries_used = subscriptionUpdates.usage.premiumQueriesUsed;
        if (subscriptionUpdates.usage.aiQueriesResetDate) updateData.ai_queries_reset_date = subscriptionUpdates.usage.aiQueriesResetDate;
      }
      
      // Only update if there are changes
      if (Object.keys(updateData).length > 0) {
        const { error: subscriptionError } = await supabase
          .from('user_subscriptions')
          .update(updateData)
          .eq('id', user.id);

        if (subscriptionError) {
          throw subscriptionError;
        }
      }

      // Update local user state
      const updatedUser = {
        ...user,
        subscription: {
          ...user.subscription,
          ...subscriptionUpdates,
          usage: {
            ...user.subscription.usage,
            ...(subscriptionUpdates.usage || {})
          }
        }
      };
      
      setUser(updatedUser);
    } catch (err: any) {
      console.error('Error updating subscription:', err);
      setError(err.message);
    }
  };

  const canUseAI = (type: 'premium' | 'standard' = 'standard'): boolean => {
    if (!user) return false;
    
    // Unlimited users have unlimited everything
    if (user.subscription.plan === 'unlimited') return true;
    
    // Check if it's a new day and reset counter
    const today = new Date();
    const resetDate = new Date(user.subscription.usage.aiQueriesResetDate);
    
    if (today.toDateString() !== resetDate.toDateString()) {
      updateSubscription({
        usage: {
          ...user.subscription.usage,
          aiQueriesUsed: 0,
          premiumQueriesUsed: 0,
          aiQueriesResetDate: today
        }
      });
      return true;
    }
    
    if (type === 'premium') {
      // Premium queries are only available for premium and unlimited users
      if (user.subscription.plan === 'free') return false;
      if (user.subscription.plan === 'premium') {
        return user.subscription.usage.premiumQueriesUsed < 20;
      }
    } else {
      // Standard queries
      if (user.subscription.plan === 'free') {
        return user.subscription.usage.aiQueriesUsed < 10;
      }
      // Premium users have unlimited standard queries
      if (user.subscription.plan === 'premium') return true;
    }
    
    return false;
  };

  const canUploadFile = (fileSize: number): boolean => {
    if (!user) return false;
    
    // Unlimited users have unlimited storage
    if (user.subscription.plan === 'unlimited') return true;
    
    const fileSizeInMB = fileSize / (1024 * 1024);
    
    if (user.subscription.plan === 'free') {
      // Free users have 500MB limit
      return (user.subscription.usage.storageUsed + fileSizeInMB) <= 500;
    } else if (user.subscription.plan === 'premium') {
      // Premium users have 5GB limit
      return (user.subscription.usage.storageUsed + fileSizeInMB) <= 5120; // 5GB = 5120MB
    }
    
    return false;
  };

  const incrementAIUsage = async (type: 'premium' | 'standard' = 'standard') => {
    if (!user || user.subscription.plan === 'unlimited') return;
    
    try {
      // Call the Supabase function to increment usage
      const { data, error } = await supabase.rpc(
        'increment_ai_usage',
        { 
          user_uuid: user.id,
          is_premium: type === 'premium'
        }
      );
      
      if (error) throw error;
      
      // Update local state
      if (type === 'premium') {
        updateSubscription({
          usage: {
            ...user.subscription.usage,
            premiumQueriesUsed: user.subscription.usage.premiumQueriesUsed + 1
          }
        });
      } else {
        if (user.subscription.plan === 'free') {
          updateSubscription({
            usage: {
              ...user.subscription.usage,
              aiQueriesUsed: user.subscription.usage.aiQueriesUsed + 1
            }
          });
        }
        // Premium users have unlimited standard queries, so no increment needed
      }
    } catch (err: any) {
      console.error('Error incrementing AI usage:', err);
      setError(err.message);
    }
  };

  const addStorageUsage = async (sizeInBytes: number) => {
    if (!user) return;
    
    try {
      // Storage usage is automatically updated by the trigger when documents are added/updated/deleted
      // Just update the local state for immediate feedback
      const sizeInMB = sizeInBytes / (1024 * 1024);
      updateSubscription({
        usage: {
          ...user.subscription.usage,
          storageUsed: user.subscription.usage.storageUsed + sizeInMB
        }
      });
    } catch (err: any) {
      console.error('Error updating storage usage:', err);
      setError(err.message);
    }
  };

  // Botón temporal de debug para ver la sesión actual en consola y alert
  if (typeof window !== 'undefined') {
    window.showSupabaseSession = async () => {
      const result = await supabase.auth.getSession();
      alert(JSON.stringify(result, null, 2));
      console.log('Supabase session:', result);
    };
  }

  return (
    <AuthContext.Provider value={{
      user,
      login,
      signup,
      logout,
      forceClearAllData,
      updateUser,
      updateSubscription,
      isLoading,
      canUseAI,
      canUploadFile,
      incrementAIUsage,
      addStorageUsage,
      error,
      setError,
      setIsLoading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Declaración para evitar error de linter al extender window
declare global {
  interface Window {
    showSupabaseSession: () => void;
  }
}